import { DreamBlock } from '@typedream/core';
import {
  focusEditor,
  getNode,
  getParentNode,
  hasNode,
  insertNodes,
  insertText,
  mergeDeepToNodes,
  removeNodes,
  replaceNodeChildren,
  setNodes,
  TNodeEntry,
  unsetNodes,
  withoutNormalizing,
  wrapNodes,
} from '@udecode/plate-core';
import copy from 'copy-to-clipboard';
import {
  cloneDeep, isElement, isEqual, omit,
} from 'lodash';
import { Path, Point, Range } from 'slate';
import { v4 as uuidv4 } from 'uuid';
import { unwrapList } from '../nodes/list/transforms';
import { DreamElementKey, DreamNode } from '../plate';
import {
  ELEMENT_ACCORDION_ITEM,
  ELEMENT_LI,
  ELEMENT_LIC,
  ELEMENT_PARAGRAPH,
} from '../pluginKeys';
import { getSelectedElementEntry } from '../plugins/elementSelection/queries';
import { selectElement } from '../plugins/elementSelection/transforms';
import { DreamEditor } from '../types';
import { validateURL } from '../utils/url';
import {
  canInsertBlock,
  canRemoveBlock,
  getAddNewBlockPath,
  getBlockFormat,
  isBlockEmpty,
  isLinkableElement,
} from './queries';
import { removeStyleAtSelection } from '../nodes/text/transforms';

export function setBlockType(
  editor: DreamEditor,
  type: DreamBlock['type'],
  options?: { at: Path },
) {
  const currentEntry = options?.at && hasNode(editor, options.at)
    ? ([getNode(editor, options.at), options.at] as TNodeEntry<DreamBlock>)
    : getSelectedElementEntry(editor);

  if (!currentEntry) return;

  const [currentBlock, currentPath] = currentEntry;

  const formatOfTarget = getBlockFormat(editor, { type });

  if (!formatOfTarget) return;

  const { formatType } = formatOfTarget;

  // if formatOfTarget is a function, call it to get the format
  const validProperties = typeof formatOfTarget.validProperties === 'function'
    ? formatOfTarget.validProperties(currentEntry[0])
    : formatOfTarget.validProperties;

  const currentType = currentEntry[0].type || ELEMENT_PARAGRAPH;
  const currentFormat = getBlockFormat(editor, { type: currentType });
  const currentFormatType = currentFormat?.formatType || 'block';

  if (currentFormatType === 'list') {
    // Turning into a list: try to unwrap the list first
    unwrapList(editor, { at: currentPath });
  }

  if (formatType === 'block') {
    // Change block type

    const properties = {
      type,
      ...(formatOfTarget.insertNode
        ? omit(formatOfTarget.insertNode, ['type', 'children'])
        : formatOfTarget.setBlockProperties?.(currentBlock || {})),
      // remove invalid properties by setting it's value to undefined
      ...Object.fromEntries(
        Object.keys(currentBlock || {})
          .filter(
            (k) => !['id', 'type'].includes(k)
              && !validProperties.includes(k as DreamElementKey),
          )
          .map((k) => [k, undefined]),
      ),
    };

    withoutNormalizing(editor, () => {
      setNodes(editor, properties, { at: currentPath });
      removeStyleAtSelection(editor, ['fontStyle', 'fontWeight', 'fontSize', 'letterSpacing', 'lineHeight']);

      if (
        formatOfTarget.insertNode
        && formatOfTarget.insertNode.children.length > 0
      ) {
        replaceNodeChildren(editor, {
          at: currentPath,
          nodes: formatOfTarget.insertNode.children,
        });
      }

      // If block type has default texts, enter
      // the default text if block is empty
      if (formatOfTarget.text) {
        if (isBlockEmpty(editor, currentBlock)) {
          insertText(editor, formatOfTarget.text, { at: currentPath });
        }
      }
    });
  } else if (formatType === 'list') {
    withoutNormalizing(editor, () => {
      // make into a lic
      setNodes(editor, { type: ELEMENT_LIC }, { at: currentPath });
      // Then wrap with the new list type
      wrapNodes(editor, { type, children: [] }, { at: currentPath });
    });
  } else if (formatType === 'command') {
    const nodeToBeInserted = formatOfTarget.insertNode;
    if (!nodeToBeInserted) return;

    withoutNormalizing(editor, () => {
      insertNodes(editor, nodeToBeInserted, { at: currentPath });
    });
  }
}

export function updateElementLink(
  editor: DreamEditor,
  data: { url: string, pageID?: string, pagePath?: string },
  options: { at: Path },
) {
  const node = getNode<DreamBlock>(editor, options.at);

  if (!node || !isLinkableElement(node)) return;

  // handle internal link
  if (data.pageID && typeof data.pagePath === 'string') {
    const internalLinkProps = {
      [node.type === 'img' ? 'href' : 'url']: data.pagePath,
      internal_link: true,
      page_id: data.pageID,
    };
    setNodes(editor, internalLinkProps, options);
    return;
  }

  const properties = {
    [node.type === 'img' ? 'href' : 'url']: validateURL(data.url),
  };

  setNodes(editor, properties, options);
}

export function removeElementLink(editor: DreamEditor, options: { at: Path }) {
  const element = getNode<DreamBlock>(editor, options.at);

  if (element && isLinkableElement(element)) {
    const props: DreamElementKey[] = [
      element.type === 'img' ? 'href' : 'url',
      'internal_link',
      'open_in_new_tab',
    ];

    unsetNodes(
      editor,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      props as any,
      { at: options.at },
    );
  }
}

export function copyBlocks(editor: DreamEditor, at?: Path | Path[]) {
  let copyPath = at;
  if (!copyPath) {
    copyPath = editor.store.getState().selectedElementPaths;
  }
  if (!copyPath || copyPath.length === 0) return;

  // These blocks should not be copied standalone, but should be with their parent element type:
  // li, accordion_item
  const getNodeAndWrapWithParentIfNeeded = (path: Path) => {
    const node = getNode<DreamBlock>(editor, path);
    if (!node) return null;

    const shouldWrap = [ELEMENT_ACCORDION_ITEM, ELEMENT_LI].includes(node.type);
    if (!shouldWrap) return node;

    const parentEntry = getParentNode(editor, path);
    if (!parentEntry) return null;

    return {
      ...parentEntry[0],
      children: [node],
    };
  };

  copy(
    JSON.stringify({
      copyingElement: true,
      element: (Path.isPath(copyPath) ? [copyPath] : copyPath)
        .map(getNodeAndWrapWithParentIfNeeded)
        .filter(Boolean),
    }),
  );
}

export function duplicateBlock(editor: DreamEditor, at?: Path) {
  let duplicatePath = at;
  if (!duplicatePath) {
    duplicatePath = getSelectedElementEntry(editor)?.[1];
  }
  if (!duplicatePath) return;

  const duplicateToPath = Path.next(duplicatePath);

  if (!canInsertBlock(editor, duplicatePath)) return;

  const node = cloneDeep(getNode<DreamBlock>(editor, duplicatePath));
  if (!node) return;

  mergeDeepToNodes({
    node,
    source: () => ({ id: uuidv4() }),
    query: {
      filter: (entry) => isElement(entry[0]),
    },
  });

  insertNodes(editor, node, { at: duplicateToPath, select: true });
}

export function deleteBlock(editor: DreamEditor, at?: Path) {
  let deletePath = at;
  if (!deletePath) {
    deletePath = getSelectedElementEntry(editor)?.[1];
  }
  if (!deletePath) return;
  if (
    isEqual(deletePath, [0, 0])
    && editor.children?.[0]?.children?.length === 1
  ) {
    return;
  }

  if (!canRemoveBlock(editor, deletePath)) return;

  removeNodes(editor, { at: deletePath });
  focusEditor(editor);
}

export function addNewBlock(editor: DreamEditor, at?: Path) {
  const insertPath = at || getAddNewBlockPath(editor);

  if (!canInsertBlock(editor, insertPath)) return;

  insertNodes(
    editor,
    { type: ELEMENT_PARAGRAPH, children: [{ text: '/' }] },
    {
      at: insertPath,
      select: true,
    },
  );

  selectElement(editor, insertPath);
  editor.store.setState({ isSlashCommandOpen: true });

  focusEditor(editor);

  // trigger onChange to get slash command menu to properly show up
  requestIdleCallback(() => editor.onChange());
}

export function setBlockProperty(
  editor: DreamEditor,
  key: string, // Should make it as enums
  value: any,
  options?: {
    at?: Path | Point | Range | undefined;
    match?: ((node: DreamNode) => boolean) | undefined;
    mode?: 'highest' | 'lowest' | 'all' | undefined;
    hanging?: boolean | undefined;
    split?: boolean | undefined;
    voids?: boolean | undefined;
  },
) {
  setNodes(
    editor,
    {
      [key]: value,
    },
    options,
  );
  focusEditor(editor);
}
